import React from 'react';
import {Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

/**
 * Generic "add source" trigger: opens a menu of mutually exclusive, equal-weight source
 * types (Files / URL / Raw text / API, etc.) rather than defaulting to one type on a
 * single click. Shared by the analyst Sources panel and the data collector's Collection
 * Material control so both surfaces get identical menu wiring/a11y (aria-haspopup,
 * aria-expanded, keyboard navigation via MUI Menu) for free.
 *
 * Renders as a small icon button (existing Sources panel affordance) when `icon` is
 * given, or as a labelled outlined button with a dropdown chevron (existing "Add
 * Material" affordance) when `label` is given.
 */
export default function AddSourceMenuButton({
    options,
    onSelect,
    icon,
    label,
    ariaLabel = 'add source',
    tooltip,
    disabled = false,
    buttonProps = {},
}) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = React.useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = React.useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleSelect = React.useCallback((key) => {
        handleClose();
        onSelect?.(key);
    }, [handleClose, onSelect]);

    const trigger = label ? (
        <Button
            variant="outlined"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={handleOpen}
            endIcon={<ArrowDropDownIcon/>}
            disabled={disabled}
            {...buttonProps}
        >
            {label}
        </Button>
    ) : (
        <IconButton
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={handleOpen}
            size="small"
            disabled={disabled}
            {...buttonProps}
        >
            {icon}
        </IconButton>
    );

    return (
        <>
            {tooltip ? (
                <Tooltip title={tooltip}>
                    <span>{trigger}</span>
                </Tooltip>
            ) : trigger}
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {options.map((option) => (
                    <MenuItem key={option.key} onClick={() => handleSelect(option.key)}>
                        {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                        <ListItemText primary={option.label}/>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
